/**
 * 管理后台API服务
 */
import { request } from '@/api/client'
import { API_ENDPOINTS as apiEndpoints } from '@/api/endpoints'

export interface VocabularyParams {
  page?: number
  page_size?: number
}

export interface VocabularyWord {
  lexeme_id: number
  word: string
  familiarity: number
  updated_at: string
}

export interface UserVocabularyData {
  user: { id: number; username: string; display_name: string | null; email: string | null }
  summary: { total_words: number; familiarity_score: number }
  vocabulary: VocabularyWord[]
  pagination: { total: number; page: number; page_size: number; total_pages: number }
}

export interface WordEvidence {
  evidence_id: number
  event_type: string
  source_episode_id: number
  occurred_at: string
  occurrence_count: number
  context: Record<string, unknown>
  contribution: number
}

export interface WordEvidenceData {
  lexeme_id: number
  word: string
  score: number
  evidence: WordEvidence[]
}

export interface ClearUserDataResult {
  user_id: number
  deleted_stats: { vocabulary_count: number; evidence_count: number; theme_instances_count: number }
  total_records_deleted: number
}

export interface AuditLogParams {
  admin_id?: number
  action?: string
  target_type?: string
  success?: boolean
  start_date?: string
  end_date?: string
  page?: number
  page_size?: number
}

export const adminApi = {
  /**
   * 获取用户词汇列表
   */
  getUserVocabulary(userId: number, params?: VocabularyParams) {
    return request.get<UserVocabularyData>(apiEndpoints.admin.userVocabulary(userId), { params })
  },

  /** 获取单词的熟悉度分值与证据快照。 */
  getWordEvidence(userId: number, lexemeId: number) {
    return request.get<WordEvidenceData>(apiEndpoints.admin.wordEvidence(userId, lexemeId))
  },

  /**
   * 一键清除用户所有学习数据 (调试功能)
   * ⚠️ 危险操作：不可恢复！
   */
  clearAllUserData(userId: number) {
    return request.delete<ClearUserDataResult>(apiEndpoints.admin.clearAllUserData(userId))
  },

  /**
   * 获取管理概览数据
   */
  getOverview() {
    return request.get(apiEndpoints.admin.overview)
  },

  /**
   * 获取用户详情
   */
  getUserDetail(userId: number) {
    return request.get(apiEndpoints.admin.userDetail(userId))
  },

  /**
   * 获取审计日志列表
   */
  getAuditLogs(params?: AuditLogParams) {
    return request.get(apiEndpoints.AUDIT_LOGS.LIST, { params })
  },

  /**
   * 获取所有操作类型（用于筛选）
   */
  getAuditLogActions() {
    return request.get(apiEndpoints.AUDIT_LOGS.ACTIONS)
  },

  /**
   * 获取所有目标类型（用于筛选）
   */
  getAuditLogTargetTypes() {
    return request.get(apiEndpoints.AUDIT_LOGS.TARGET_TYPES)
  },

  // ========== 管理员管理 ==========

  /**
   * 获取管理员列表
   */
  getAdminList(params?: {
    page?: number
    page_size?: number
    search?: string
    role?: string
    is_active?: boolean
  }) {
    return request.get(apiEndpoints.ADMIN_MANAGEMENT.LIST, { params })
  },

  /**
   * 获取管理员详情
   */
  getAdminDetail(id: number) {
    return request.get(apiEndpoints.ADMIN_MANAGEMENT.DETAIL(id))
  },

  /**
   * 创建管理员
   */
  createAdmin(data: {
    username: string
    email: string
    password: string
    display_name?: string
    role: string
    permissions?: string[]
  }) {
    return request.post(apiEndpoints.ADMIN_MANAGEMENT.CREATE, data)
  },

  /**
   * 更新管理员基本信息
   */
  updateAdmin(id: number, data: {
    email?: string
    display_name?: string
    avatar?: string
  }) {
    return request.put(apiEndpoints.ADMIN_MANAGEMENT.UPDATE(id), data)
  },

  /**
   * 删除管理员（软删除）
   */
  deleteAdmin(id: number) {
    return request.delete(apiEndpoints.ADMIN_MANAGEMENT.DELETE(id))
  },

  /**
   * 更新管理员角色
   */
  updateAdminRole(id: number, role: string) {
    return request.put(apiEndpoints.ADMIN_MANAGEMENT.UPDATE_ROLE(id), { role })
  },

  /**
   * 更新管理员权限
   */
  updateAdminPermissions(id: number, permissions: string[], mode: 'replace' | 'add' | 'remove' = 'replace') {
    return request.put(apiEndpoints.ADMIN_MANAGEMENT.UPDATE_PERMISSIONS(id), { permissions, mode })
  },

  /**
   * 更新管理员状态（启用/禁用）
   */
  updateAdminStatus(id: number, isActive: boolean, reason?: string) {
    return request.put(apiEndpoints.ADMIN_MANAGEMENT.UPDATE_STATUS(id), {
      is_active: isActive,
      reason
    })
  },

  /**
   * 重置管理员密码
   */
  resetAdminPassword(id: number, newPassword: string) {
    return request.post(apiEndpoints.ADMIN_MANAGEMENT.RESET_PASSWORD(id), {
      new_password: newPassword
    })
  },

  /**
   * 更新个人信息
   */
  updateProfile(data: {
    email?: string
    display_name?: string
    avatar?: string
    current_password?: string
    new_password?: string
  }) {
    return request.put(apiEndpoints.ADMIN_MANAGEMENT.UPDATE_PROFILE, data)
  }
}